import { useCallback, useEffect, useState } from 'react';

/**
 * Reports the *visible* viewport (the visual viewport), as opposed to the layout viewport returned by
 * `useWindowSize`. These differ on iOS while the soft keyboard is open (and on any platform while pinch-zoomed):
 * the layout viewport — and therefore `window.innerHeight`/`innerWidth` — stays full size, while the visual
 * viewport shrinks and shifts. Use this when you need the area the user can actually see, e.g. to keep a popover
 * out from behind the keyboard. Falls back to the layout viewport when `window.visualViewport` is unavailable.
 */
export default function useVisualViewport() {
  const [size, setSize] = useState(getSize);

  const onResize = useCallback(() => setSize(getSize()), []);
  useEffect(() => {
    window.addEventListener('resize', onResize);
    const vv = window.visualViewport;
    // On iOS, opening/closing the keyboard shifts the visual viewport without firing a window `resize`.
    if (vv) {
      vv.addEventListener('resize', onResize);
      vv.addEventListener('scroll', onResize);
    }
    return () => {
      window.removeEventListener('resize', onResize);
      if (vv) {
        vv.removeEventListener('resize', onResize);
        vv.removeEventListener('scroll', onResize);
      }
    };
  }, [onResize]);

  return size;
}

function getSize() {
  const vv = window.visualViewport;
  return {
    height: vv ? vv.height : window.innerHeight,
    offsetLeft: vv ? vv.offsetLeft : 0,
    offsetTop: vv ? vv.offsetTop : 0,
    width: vv ? vv.width : window.innerWidth,
  };
}
